import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "@apollo/client";
import { useEffect, useCallback, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { GET_PRODUCT_VARIANTS } from "@/pages/events/queries";
import Select from "@/components/Select";
import { FormValues } from "../validation/schema";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { useHasEventDefaults } from "../FormVisibilityContext";

interface Variant {
  id: string;
  name: string;
  priceCents: string;
  lookupKey?: string;
  externalId?: string | null;
}

interface VariantOption extends Variant {
  value: string;
  displayText: string;
}

interface VariantsDropdownProps {
  index: number;
  productId: string;
  disabled?: boolean;
  isNationalPATFee?: boolean;
  isEditing?: boolean;
}

export const PAT_NATIONAL_FEE_LOOKUP_KEY = "pat-pga";

const selectVariantBySessionYear = (sessionDate: string, variants: any[]) => {
  const sessionYear = new Date(sessionDate).getFullYear();

  const variantSelected = variants.find((variant) => variant.externalId?.endsWith(`PAT-Registration-${sessionYear}`));
  return variantSelected || variants.find((variant) => variant.externalId?.endsWith(`PAT-Registration`));
};

export const getPATNationalFeeYear = (sessions?: { date?: { startDate?: string } }[]) => {
  const startDates = (sessions ?? [])
    .map((session) => DateTime.fromISO(session?.date?.startDate ?? ""))
    .filter((date) => date.isValid);

  if (startDates.length === 0) return undefined;

  return startDates.reduce((earliest, date) => (date < earliest ? date : earliest)).year;
};

const PAT_NATIONAL_FEE_LOOKUP_KEY_YEAR = new RegExp(`^${PAT_NATIONAL_FEE_LOOKUP_KEY}-(\\d{4})$`);
const PAT_NATIONAL_FEE_EXTERNAL_ID_YEAR = /PAT-Registration-(\d{4})$/;

const getPATNationalFeeEffectiveYear = (variant: Pick<Variant, "lookupKey" | "externalId">) => {
  const match =
    variant.lookupKey?.match(PAT_NATIONAL_FEE_LOOKUP_KEY_YEAR) ??
    variant.externalId?.match(PAT_NATIONAL_FEE_EXTERNAL_ID_YEAR);
  return match ? Number(match[1]) : undefined;
};

// A dated variant applies from its year until a later dated variant takes over, so a new variant is only needed
// when the price changes. The undated generic variant never matches.
export const findPATNationalFeeVariant = <T extends Pick<Variant, "lookupKey" | "externalId">>(
  variants: T[],
  year: number
) => {
  let effective: { variant: T; year: number } | undefined;

  for (const variant of variants) {
    const effectiveYear = getPATNationalFeeEffectiveYear(variant);
    if (effectiveYear === undefined || effectiveYear > year) continue;
    if (!effective || effectiveYear > effective.year) effective = { variant, year: effectiveYear };
  }

  return effective?.variant;
};

export const missingPATNationalFeeMessage = (year: number) =>
  `No national PAT fee is set up for ${year}. Ask an admin to add it before saving this event.`;

export const PAT_NATIONAL_FEE_LOAD_ERROR = "Couldn't load the national PAT fee. Refresh the page and try again.";

const VariantsDropdown = ({
  index,
  productId,
  disabled,
  isNationalPATFee = false,
  isEditing = false
}: VariantsDropdownProps) => {
  const {
    control,
    watch,
    clearErrors,
    trigger,
    setValue,
    formState: { errors }
  } = useFormContext<FormValues>();

  const sessions = watch("sessions");
  const sessionDate = sessions?.[0]?.date?.startDate;
  const nationalFeeYear = isNationalPATFee ? getPATNationalFeeYear(sessions) : undefined;
  const hasEventDefaults = useHasEventDefaults();
  const [missingFeeYear, setMissingFeeYear] = useState<number>();

  const setVariant = useCallback(
    (variant: Variant) => {
      setValue(`products.${index}.variantId`, variant.id);
      setValue(`products.${index}.variantName`, variant.name);
      setValue(`products.${index}.variantPrice`, parseInt(variant.priceCents));
    },
    [setValue, index]
  );

  const clearVariant = useCallback(() => {
    setValue(`products.${index}.variantId`, "");
    setValue(`products.${index}.variantName`, "");
    setValue(`products.${index}.variantPrice`, 0);
  }, [setValue, index]);

  const applyPATNationalFee = useCallback(
    (variants: Variant[]) => {
      if (!nationalFeeYear) {
        setMissingFeeYear(undefined);
        return;
      }

      const matchingVariant = findPATNationalFeeVariant(variants, nationalFeeYear);
      const currentVariantId = watch(`products.${index}.variantId`);

      if (matchingVariant) {
        setMissingFeeYear(undefined);
        if (currentVariantId !== matchingVariant.id) {
          setVariant(matchingVariant);
          trigger(`products.${index}.variantId`);
        }
        return;
      }

      // Edits load the saved fee with no variant selected; keep it so past-year events without a variant stay editable.
      if (isEditing && !currentVariantId) return;

      setMissingFeeYear(nationalFeeYear);
      clearVariant();
    },
    [nationalFeeYear, watch, index, isEditing, setVariant, clearVariant, trigger]
  );

  const { data: variantData, error: variantsError } = useQuery(GET_PRODUCT_VARIANTS, {
    variables: { productId },
    onCompleted: (data) => {
      if (isNationalPATFee) return;

      const variants = data?.commerceProductVariants?.nodes || [];
      const currentVariantId = watch(`products.${index}.variantId`);

      if (variants.length > 0 && !currentVariantId) {
        const selectedVariant = selectVariantBySessionYear(sessionDate, variants);
        setVariant(selectedVariant);
        trigger(`products.${index}.variantId`);
      }
    }
  });

  const variantOptions = useMemo(
    () =>
      variantData?.commerceProductVariants.nodes.map(
        (variant: Variant): VariantOption => ({
          id: variant.id,
          value: variant.id,
          displayText: centsToDollarsFormatted(parseInt(variant.priceCents)) ?? "",
          name: variant.name,
          priceCents: variant.priceCents,
          lookupKey: variant.lookupKey,
          externalId: variant.externalId
        })
      ) || [],
    [variantData?.commerceProductVariants.nodes]
  );

  useEffect(() => {
    if (isNationalPATFee) {
      if (variantsError) {
        if (!isEditing) clearVariant();
      } else if (variantData) {
        applyPATNationalFee(variantOptions);
      }
      return;
    }

    if (variantOptions.length > 0 && sessionDate) {
      const selectedVariant = selectVariantBySessionYear(sessionDate, variantOptions);
      const currentVariantId = watch(`products.${index}.variantId`);
      if (currentVariantId !== selectedVariant?.id && !hasEventDefaults) {
        setVariant(selectedVariant);
        trigger(`products.${index}.variantId`);
      }
    }
  }, [
    isNationalPATFee,
    applyPATNationalFee,
    clearVariant,
    isEditing,
    variantData,
    variantsError,
    sessionDate,
    variantOptions,
    index,
    watch,
    setValue,
    trigger,
    setVariant,
    hasEventDefaults
  ]);

  const missingFeeMessage = !isNationalPATFee
    ? undefined
    : variantsError
      ? PAT_NATIONAL_FEE_LOAD_ERROR
      : missingFeeYear
        ? missingPATNationalFeeMessage(missingFeeYear)
        : undefined;

  return (
    <Controller
      name={`products.${index}.variantId`}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Select
          label="Price"
          options={variantOptions}
          className="w-full"
          value={value}
          disabled={disabled}
          onSelect={(value) => {
            onChange(value);
            const selectedVariant = variantOptions.find((option: VariantOption) => option.value === value);
            setVariant(selectedVariant || variantOptions[0]);

            if (value && errors.products?.[index]?.variantId) {
              clearErrors(`products.${index}.variantId`);
            }
            if (!value) {
              trigger(`products.${index}.variantId`);
            }
          }}
          required
          data-testid={`variant-select-${index}`}
          error={!!missingFeeMessage || !!errors.products?.[index]?.variantId}
          errorText={missingFeeMessage ?? (errors.products?.[index]?.variantId?.message as string)}
        />
      )}
    />
  );
};

export default VariantsDropdown;
